import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Search, Plus, Building2, Users, Pencil, Trash2, ArrowLeft,
  UserPlus, Shield, X, Store, BookOpen, Truck, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const CAPABILITY_OPTIONS = [
  { value: 'publisher', label: 'Éditeur', icon: BookOpen },
  { value: 'distributor', label: 'Distributeur', icon: Truck },
  { value: 'retailer', label: 'Magasin', icon: Store },
  { value: 'other', label: 'Autre', icon: Building2 },
];

const AVAILABLE_PERMISSIONS = [
  { key: 'catalog.view', label: 'Voir le catalogue' },
  { key: 'offers.view', label: 'Voir les offres' },
  { key: 'org.profile.edit', label: 'Modifier la fiche entreprise' },
  { key: 'products.create', label: 'Créer des produits' },
  { key: 'products.edit', label: 'Modifier des produits' },
  { key: 'distribution.view', label: 'Voir les distributions / commandes' },
  { key: 'distribution.manage', label: 'Gérer les distributions / commandes' },
];

const ALL_PERMISSION_KEYS = AVAILABLE_PERMISSIONS.map(p => p.key);

/* ─────────────────── MAIN COMPONENT ─────────────────── */

const AdminOrganizations = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // Detail view
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Create/Edit dialog
  const [formMode, setFormMode] = useState(null); // 'create' | 'edit' | null
  const [formData, setFormData] = useState({ nom: '', type: 'partner', capabilities: [], description: '', contact_email: '', telephone: '', siret: '' });

  // Add member
  const [showAddMember, setShowAddMember] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [memberPerms, setMemberPerms] = useState([...ALL_PERMISSION_KEYS]);
  const [memberRole, setMemberRole] = useState('member');

  // Delete confirm
  const [deleteMember, setDeleteMember] = useState(null);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (typeFilter !== 'all') params.type = typeFilter;
      const res = await api.get('/admin/organizations', { params });
      setOrgs(res.data);
    } catch { toast.error('Erreur chargement organisations'); }
    finally { setLoading(false); }
  }, [search, typeFilter]);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const loadOrgDetail = async (orgId) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/organizations/${orgId}`);
      setSelectedOrg(res.data);
    } catch { toast.error('Erreur chargement détail'); }
    finally { setDetailLoading(false); }
  };

  /* ── Create / Edit org ── */
  const openCreate = () => {
    setFormData({ nom: '', type: 'partner', capabilities: [], description: '', contact_email: '', telephone: '', siret: '' });
    setFormMode('create');
  };
  const openEdit = () => {
    if (!selectedOrg) return;
    setFormData({
      nom: selectedOrg.nom || '',
      type: selectedOrg.type || 'partner',
      capabilities: selectedOrg.capabilities || [],
      description: selectedOrg.description || '',
      contact_email: selectedOrg.contact_email || '',
      telephone: selectedOrg.telephone || '',
      siret: selectedOrg.siret || '',
    });
    setFormMode('edit');
  };
  const handleFormSubmit = async () => {
    if (!formData.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    try {
      if (formMode === 'create') {
        const res = await api.post('/admin/organizations', formData);
        toast.success(`Organisation "${res.data.nom}" créée`);
        setFormMode(null);
        loadOrgs();
      } else {
        const res = await api.put(`/admin/organizations/${selectedOrg.id}`, formData);
        toast.success('Organisation mise à jour');
        setFormMode(null);
        setSelectedOrg({ ...selectedOrg, ...res.data, members: selectedOrg.members });
        loadOrgs();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const toggleCapability = (cap) => {
    setFormData(prev => ({
      ...prev,
      capabilities: prev.capabilities.includes(cap)
        ? prev.capabilities.filter(c => c !== cap)
        : [...prev.capabilities, cap],
    }));
  };

  /* ── Add member ── */
  const searchUsers = async (q) => {
    setUserSearch(q);
    if (q.length < 2) { setUserResults([]); return; }
    try {
      const res = await api.get(`/admin/users/search?q=${encodeURIComponent(q)}`);
      setUserResults(res.data);
    } catch { /* ignore */ }
  };

  const addMember = async (userId) => {
    try {
      await api.post(`/admin/organizations/${selectedOrg.id}/members`, {
        user_id: userId,
        role_in_org: memberRole,
        permissions: memberPerms,
      });
      toast.success('Membre ajouté');
      setShowAddMember(false);
      setUserSearch('');
      setUserResults([]);
      setMemberPerms([...ALL_PERMISSION_KEYS]);
      loadOrgDetail(selectedOrg.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const removeMember = async () => {
    if (!deleteMember) return;
    try {
      await api.delete(`/admin/memberships/${deleteMember.membership_id}`);
      toast.success('Membre retiré');
      setDeleteMember(null);
      loadOrgDetail(selectedOrg.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const toggleMemberPerm = (mbId, perm, currentPerms) => {
    const newPerms = currentPerms.includes(perm)
      ? currentPerms.filter(p => p !== perm)
      : [...currentPerms, perm];
    api.put(`/admin/memberships/${mbId}`, { permissions: newPerms })
      .then(() => { loadOrgDetail(selectedOrg.id); })
      .catch(() => toast.error('Erreur mise à jour'));
  };

  /* ─────────────── DETAIL VIEW ─────────────── */
  if (selectedOrg) {
    return (
      <div data-testid="admin-org-detail">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => setSelectedOrg(null)} data-testid="back-to-list">
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour
          </Button>
          <h1 className="text-2xl font-bold">{selectedOrg.nom}</h1>
          <Badge variant={selectedOrg.type === 'partner' ? 'default' : 'secondary'}>
            {selectedOrg.type === 'partner' ? 'Entreprise' : 'Magasin'}
          </Badge>
          {(selectedOrg.capabilities || []).map(c => (
            <Badge key={c} variant="outline" className="text-xs">
              {CAPABILITY_OPTIONS.find(o => o.value === c)?.label || c}
            </Badge>
          ))}
          <div className="ml-auto">
            <Button size="sm" variant="outline" onClick={openEdit} data-testid="edit-org-btn">
              <Pencil className="h-3.5 w-3.5 mr-1" /> Modifier
            </Button>
          </div>
        </div>

        {/* Org Info */}
        <Card className="mb-6">
          <CardContent className="pt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-muted-foreground">Slug :</span> <span className="font-mono">{selectedOrg.slug}</span></div>
            <div><span className="text-muted-foreground">Statut :</span> {selectedOrg.status}</div>
            <div><span className="text-muted-foreground">Email :</span> {selectedOrg.contact_email || '—'}</div>
            <div><span className="text-muted-foreground">Tél :</span> {selectedOrg.telephone || '—'}</div>
            {selectedOrg.description && <div className="col-span-4 text-muted-foreground">{selectedOrg.description}</div>}
          </CardContent>
        </Card>

        {/* Members */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" /> Membres ({selectedOrg.members?.length || 0})
            </CardTitle>
            <Button size="sm" onClick={() => setShowAddMember(true)} data-testid="add-member-btn">
              <UserPlus className="h-3.5 w-3.5 mr-1" /> Ajouter un membre
            </Button>
          </CardHeader>
          <CardContent>
            {detailLoading ? <p className="text-muted-foreground py-4">Chargement...</p> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Utilisateur</TableHead>
                    <TableHead>Rôle global</TableHead>
                    <TableHead>Rôle dans l'org</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(selectedOrg.members || []).map(m => (
                    <TableRow key={m.membership_id}>
                      <TableCell>
                        <div className="font-medium">{m.nom || m.email}</div>
                        <div className="text-xs text-muted-foreground">{m.email}</div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{m.user_role}</Badge></TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">{m.role_in_org}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {AVAILABLE_PERMISSIONS.map(p => (
                            <button
                              key={p.key}
                              onClick={() => toggleMemberPerm(m.membership_id, p.key, m.permissions)}
                              className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
                                m.permissions.includes(p.key)
                                  ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                                  : 'bg-slate-50 border-slate-200 text-slate-400'
                              }`}
                              title={p.label}
                            >
                              {p.key.split('.').pop()}
                            </button>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={m.status === 'active' ? 'default' : 'destructive'} className="text-xs">
                          {m.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteMember(m)} data-testid={`remove-member-${m.user_id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!selectedOrg.members || selectedOrg.members.length === 0) && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Aucun membre</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add Member Panel */}
        {showAddMember && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowAddMember(false)}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Ajouter un membre</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowAddMember(false)}><X className="h-4 w-4" /></Button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Rechercher un utilisateur (email ou nom)</Label>
                  <Input
                    value={userSearch}
                    onChange={e => searchUsers(e.target.value)}
                    placeholder="ex: jean@..."
                    data-testid="member-search-input"
                  />
                  {userResults.length > 0 && (
                    <div className="border rounded-lg mt-1 max-h-40 overflow-y-auto">
                      {userResults.map(u => (
                        <button
                          key={u.id}
                          onClick={() => addMember(u.id)}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between text-sm"
                          data-testid={`select-user-${u.id}`}
                        >
                          <span>{u.nom || u.email} <span className="text-muted-foreground">({u.email})</span></span>
                          <Badge variant="outline" className="text-xs">{u.role}</Badge>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Rôle dans l'organisation</Label>
                  <Select value={memberRole} onValueChange={setMemberRole}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Propriétaire</SelectItem>
                      <SelectItem value="admin">Administrateur</SelectItem>
                      <SelectItem value="member">Membre</SelectItem>
                      <SelectItem value="viewer">Lecteur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Permissions</Label>
                  <div className="grid grid-cols-1 gap-1 mt-1">
                    {AVAILABLE_PERMISSIONS.map(p => (
                      <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded">
                        <input
                          type="checkbox"
                          checked={memberPerms.includes(p.key)}
                          onChange={() => setMemberPerms(prev =>
                            prev.includes(p.key) ? prev.filter(x => x !== p.key) : [...prev, p.key]
                          )}
                          className="rounded border-slate-300"
                        />
                        {p.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Org Form */}
        {formMode === 'edit' && <OrgFormDialog formData={formData} setFormData={setFormData} onSubmit={handleFormSubmit} onClose={() => setFormMode(null)} toggleCapability={toggleCapability} title="Modifier l'organisation" />}

        {/* Delete member confirm */}
        <AlertDialog open={!!deleteMember} onOpenChange={(open) => !open && setDeleteMember(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Retirer ce membre ?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteMember?.nom || deleteMember?.email} sera retiré de cette organisation. Cette action est réversible (vous pourrez le ré-ajouter).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={removeMember} className="bg-red-600 hover:bg-red-700">Retirer</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  /* ─────────────── LIST VIEW ─────────────── */
  return (
    <div data-testid="admin-org-list">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Organisations</h1>
        <Button onClick={openCreate} data-testid="create-org-btn">
          <Plus className="h-4 w-4 mr-1" /> Nouvelle organisation
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="org-search-input"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48" data-testid="org-type-filter">
            <SelectValue placeholder="Tous les types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="partner">Entreprises</SelectItem>
            <SelectItem value="retailer">Magasins</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Chargement...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Activités</TableHead>
                  <TableHead>Membres</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgs.map(org => (
                  <TableRow
                    key={org.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => loadOrgDetail(org.id)}
                    data-testid={`org-row-${org.id}`}
                  >
                    <TableCell>
                      <div className="font-medium">{org.nom}</div>
                      <div className="text-xs text-muted-foreground font-mono">{org.slug}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={org.type === 'partner' ? 'default' : 'secondary'}>
                        {org.type === 'partner' ? 'Entreprise' : 'Magasin'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {(org.capabilities || []).map(c => (
                          <Badge key={c} variant="outline" className="text-xs">
                            {CAPABILITY_OPTIONS.find(o => o.value === c)?.label || c}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-sm">
                        <Users className="h-3.5 w-3.5 text-muted-foreground" /> {org.member_count}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={org.status === 'active' ? 'default' : 'destructive'} className="text-xs">{org.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
                {orgs.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Aucune organisation</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="mt-3 text-sm text-muted-foreground">{orgs.length} organisation{orgs.length > 1 ? 's' : ''}</div>

      {/* Create Org Form */}
      {formMode === 'create' && <OrgFormDialog formData={formData} setFormData={setFormData} onSubmit={handleFormSubmit} onClose={() => setFormMode(null)} toggleCapability={toggleCapability} title="Nouvelle organisation" />}
    </div>
  );
};

/* ─────────────── ORG FORM DIALOG ─────────────── */

const OrgFormDialog = ({ formData, setFormData, onSubmit, onClose, toggleCapability, title }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={onClose}>
    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg">{title}</h3>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
      </div>
      <div className="space-y-4">
        <div>
          <Label>Nom *</Label>
          <Input value={formData.nom} onChange={e => setFormData(p => ({ ...p, nom: e.target.value }))} placeholder="Nom de l'organisation" data-testid="org-form-nom" />
        </div>
        <div>
          <Label>Type</Label>
          <Select value={formData.type} onValueChange={v => setFormData(p => ({ ...p, type: v }))}>
            <SelectTrigger data-testid="org-form-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="partner">Entreprise (éditeur / distributeur)</SelectItem>
              <SelectItem value="retailer">Magasin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Activités</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {CAPABILITY_OPTIONS.map(opt => {
              const Icon = opt.icon;
              const selected = formData.capabilities.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => toggleCapability(opt.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                    selected ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`}
                  data-testid={`cap-${opt.value}`}
                >
                  <Icon className="h-3.5 w-3.5" /> {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Textarea value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} rows={2} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Email de contact</Label>
            <Input value={formData.contact_email} onChange={e => setFormData(p => ({ ...p, contact_email: e.target.value }))} />
          </div>
          <div>
            <Label>Téléphone</Label>
            <Input value={formData.telephone} onChange={e => setFormData(p => ({ ...p, telephone: e.target.value }))} />
          </div>
        </div>
        <div>
          <Label>SIRET</Label>
          <Input value={formData.siret} onChange={e => setFormData(p => ({ ...p, siret: e.target.value }))} />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <Button variant="outline" onClick={onClose}>Annuler</Button>
        <Button onClick={onSubmit} data-testid="org-form-submit">Enregistrer</Button>
      </div>
    </div>
  </div>
);

export default AdminOrganizations;
